import React, { useState, useEffect } from "react";
import styles from "./AddOutputModal.module.scss";
import style from "../OutputForm.module.scss";
import store from "../../../../../reducks/store/AppTrigger/store";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { OutputValue } from "../types/types";
import axios from "axios";
import { useTranslation } from "react-i18next";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

type Props = {
  editOutput: { isEdit: boolean; index: number; output: OutputValue | null };
  setEditOutput: (editOutput: { isEdit: boolean; index: number; output: OutputValue | null }) => void;
  selectableDataType: string;
  templateClassName: string;
  outputList: OutputValue[];
  outputLabels: OutputValue[];
  transformResultData: (data: any) => string[];
};

export const EditOutput = ({
  editOutput,
  setEditOutput,
  selectableDataType,
  templateClassName,
  outputList,
  outputLabels,
  transformResultData,
}: Props) => {
  const { t } = useTranslation();

  const [displayName, setDisplayName] = useState<string>(editOutput.output?.display_name || "");
  const [dataType, setDataType] = useState<string>(editOutput.output?.data_type || "");
  const [jsonPath, setJsonPath] = useState<string>(editOutput.output?.name || "");
  const [isDuplicationChecking, setIsDuplicationChecking] = useState<boolean>(false);
  const debouncedDisplayName = useDebounce(displayName, 200);
  const debouncedJsonPath = useDebounce(jsonPath, 200);
  const isSubmitDisabled =
    !editOutput.output?.display_name ||
    editOutput.output.display_name.trim() === "" ||
    !editOutput.output?.name ||
    editOutput.output.name.trim() === "" ||
    isDuplicationChecking;

  const changeDataType = (type: string) => {
    setDataType(type);
    const _copy = JSON.parse(JSON.stringify(editOutput.output));
    if (type === "file") {
      _copy.readonly_test_value = "";
      _copy.test_value = "";
    }
    _copy.data_type = type;
    setEditOutput({ isEdit: true, index: editOutput.index, output: _copy });
  };

  const attachFileNameToForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;
    const _copy = JSON.parse(JSON.stringify(editOutput.output));
    _copy.readonly_test_value = file.name;
    _copy.test_value = file.name;
    setEditOutput({ isEdit: true, index: editOutput.index, output: _copy });
  };

  const outputDuplicationCheck = async (name: string) => {
    if (!editOutput.output || isDuplicationChecking) return;
    const outputListCopy = outputList.filter((_, i) => i !== editOutput.index);
    const combinedOutputList = outputListCopy.concat(outputLabels);
    const additionalOutputDisplayNames = combinedOutputList
      .filter((outputLabel) => !outputLabel.target_id)
      .map((outputLabel) => outputLabel.display_name);
    setIsDuplicationChecking(true);

    await axios
      .post(
        location.href.replace(
          /workflow_templates.*/,
          `workflow_templates/${window.location.pathname.split("/")[3]}/ajax_output_duplication`,
        ),
        {
          target_display_name: name,
          target_id: editOutput.output?.target_id,
          additional_output_display_names: additionalOutputDisplayNames,
        },
      )
      .then((res) => {
        const _copy = JSON.parse(JSON.stringify(editOutput.output));
        _copy.display_name = name;
        _copy.is_duplicated = res.data.is_duplicated;
        setEditOutput({ ...editOutput, index: editOutput.index, output: _copy });
        setIsDuplicationChecking(false);
      });
  };

  // jsonPathの値を取得する
  const getJsonPathValue = async (jsonPathInputValue: string) => {
    const apiRequestTestResult = $(".js-api_request_test_result").val();
    if (!jsonPathInputValue) return;
    const res = await axios.post("ajax_json_path_test", {
      api_request_test_result: apiRequestTestResult,
      json_path: jsonPathInputValue,
    });

    if (res?.data?.result) {
      const resultData = transformResultData(res.data);
      const _copy = JSON.parse(JSON.stringify(editOutput.output));
      _copy.name = jsonPathInputValue;
      _copy.test_value = resultData.join("[,]");
      _copy.readonly_test_value = resultData.join(",");
      setEditOutput({ ...editOutput, index: editOutput.index, output: _copy });
    } else {
      const _copy = JSON.parse(JSON.stringify(editOutput.output));
      _copy.name = jsonPathInputValue;
      _copy.test_value = "";
      _copy.readonly_test_value = "";
      setEditOutput({ ...editOutput, index: editOutput.index, output: _copy });
    }
  };

  const submit = () => {
    const _copy = JSON.parse(JSON.stringify(editOutput.output));
    _copy.display_name = displayName;
    _copy.data_type = dataType;
    store.dispatch({ type: "EDIT", index: editOutput.index, output: _copy });
    setEditOutput({ ...editOutput, isEdit: false });
  };

  useEffect(() => {
    if (debouncedDisplayName) {
      outputDuplicationCheck(debouncedDisplayName);
    } else {
      const _copy = JSON.parse(JSON.stringify(editOutput.output));
      _copy.display_name = debouncedDisplayName;
      _copy.is_duplicated = false;
      setEditOutput({ isEdit: true, index: editOutput.index, output: _copy });
    }
  }, [debouncedDisplayName]);

  useEffect(() => {
    if (debouncedJsonPath) {
      getJsonPathValue(debouncedJsonPath);
    } else {
      const _copy = JSON.parse(JSON.stringify(editOutput.output));
      _copy.name = debouncedJsonPath;
      _copy.test_value = "";
      _copy.readonly_test_value = "";
      setEditOutput({ ...editOutput, index: editOutput.index, output: _copy });
    }
  }, [debouncedJsonPath]);

  return (
    <>
      <div className={styles.overlay} />
      <div className={`${styles.editOutput} output-edit-modal`}>
        <div className={style.outputEditContent}>
          <>
            <div className={style.outputEditRow}>
              <div className={style.outputEditElement}>
                <div className={style.outputEditTitle}>{t("EditOutput.項目名")}</div>
                <input
                  className={`${style.outputEditInput} ${
                    editOutput.output?.is_duplicated && "output-duplication-warning"
                  } app_output_display_name-test`}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                {editOutput.output?.is_duplicated && (
                  <div className="output-duplication-warning-feedback">
                    {t(
                      "EditOutput.項目名が重複しています。項目名が重複している場合、値が上書きされますのでご注意ください。",
                    )}
                  </div>
                )}
              </div>
              <div className={style.outputEditElement}>
                <div className={style.outputEditTitle}>{t("EditOutput.入力形式")}</div>
                <select
                  className="form-control app_output_data_type-test"
                  onChange={(e) => {
                    changeDataType(e.target.value);
                  }}
                  value={dataType}
                >
                  {/* @ts-ignore */}
                  {JSON.parse(selectableDataType).map((selection, i) => (
                    <option key={i} value={selection[Object.keys(selection)[0]]}>
                      {Object.keys(selection)[0]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={style.outputEditRow}>
              {templateClassName === "api_operation_template" && dataType === "file" ? (
                <>
                  <div className={style.outputEditElement}>
                    <div
                      className="field js-workflow_template_action_output_file_field js-api_operation_attachment_template_attachment_field"
                      style={{ width: "100%" }}
                    >
                      <div className="form-group">
                        <label
                          htmlFor={`api_operation_template_workflow_template_action_attributes_workflow_template_action_outputs_attributes_${editOutput.index}_output_test_attachment_file`}
                        >
                          {t("EditOutput.添付ファイル")}
                        </label>
                        <div className="custom-file">
                          <input
                            className="custom-file-input test-workflow_template_action_output_file_form"
                            style={{ width: "100%", height: "100%" }}
                            type="file"
                            onChange={(e) => attachFileNameToForm(e)}
                          />
                          <label
                            className="custom-file-label"
                            htmlFor={`api_operation_template_workflow_template_action_attributes_workflow_template_action_outputs_attributes_${editOutput.index}_output_test_attachment_file`}
                            style={{ cursor: "pointer" }}
                          >
                            {editOutput.output?.readonly_test_value
                              ? editOutput.output.readonly_test_value
                              : t("EditOutput.ファイルを選択してください")}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={style.outputEditElement}>
                    <div className={style.outputEditTitle}>JSONPath</div>
                    <input
                      className={`${style.outputEditInput}`}
                      value={jsonPath}
                      onChange={(e) => setJsonPath(e.target.value)}
                    />
                  </div>
                  <div className={style.outputEditElement}>
                    <div className={style.outputEditTitle}>{t("EditOutput.テストで取得した値")}</div>
                    <input
                      className={`${style.outputEditInput} app_output_readonly_test_value-test`}
                      readOnly
                      style={{
                        color: "#777777",
                        backgroundColor: "#F3F3F3",
                      }}
                      value={
                        editOutput.output?.readonly_test_value
                          ? editOutput.output.readonly_test_value.replace(/\\n/g, " ")
                          : ""
                      }
                    ></input>
                  </div>
                </>
              )}
            </div>
          </>
        </div>
        <div className={style.outputEditButtonRow}>
          <div
            className="btn btn-secondary test-cancel_output_btn"
            style={{ fontSize: "16px", padding: "6px", height: "44px" }}
            onClick={() => {
              setEditOutput({ isEdit: false, index: -1, output: null });
            }}
          >
            {t("EditOutput.キャンセル")}
          </div>
          <button
            className="btn btn-info test_save_output_btn"
            style={{
              fontSize: "16px",
              padding: "6px",
              height: "44px",
            }}
            disabled={isSubmitDisabled}
            onClick={() => {
              submit();
            }}
          >
            {t("EditOutput.保存")}
          </button>
        </div>
      </div>
    </>
  );
};
